import type { Range } from "@tiptap/core"
import type { Editor } from "@tiptap/react"
import {
  getMentionResources,
  type MentionKind,
} from "~/common/media/resourceMentions"
import type { UploadedResource } from "~/common/media/uploadedResources"
import { getPromptNodeText, toMentionAttrs } from "~/common/promptDocument"
export { buildEditorContent, toMentionAttrs } from "~/common/promptDocument"
import type { MentionNodeAttrs, SuggestionState } from "./types"

export const INITIAL_SUGGESTION_STATE: SuggestionState = {
  activeIndex: 0,
  command: null,
  items: [],
  open: false,
  position: { left: 0, top: 0 },
}

export const getSuggestionPosition = (
  container: HTMLElement,
  rect: Pick<DOMRect, "left" | "bottom">,
): SuggestionState["position"] => {
  const containerRect = container.getBoundingClientRect()

  // 绝对定位锚点使用容器内容坐标，需要补回滚动量并扣除容器边框。
  return {
    left:
      rect.left -
      containerRect.left +
      container.scrollLeft -
      container.clientLeft,
    top:
      rect.bottom -
      containerRect.top +
      container.scrollTop -
      container.clientTop -
      8,
  }
}

export const getMentionNodeResources = (editor: Editor | null) => {
  if (!editor) return []

  const resourceMap = new Map<string, UploadedResource>()

  editor.state.doc.descendants((node) => {
    if (node.type.name !== "resourceMention") return true

    const attrs = node.attrs as MentionNodeAttrs
    const resource: UploadedResource = {
      id: attrs.id === attrs.url ? undefined : attrs.id,
      url: attrs.url,
      name: attrs.name,
      type: attrs.type,
      uploadedAt: Date.now(),
    }

    resourceMap.set(attrs.id, resource)
    return true
  })

  return Array.from(resourceMap.values())
}

export const insertMentionResource = (
  editor: Editor,
  mentionAttrs: MentionNodeAttrs,
  range?: Range,
) => {
  const content = [
    {
      type: "resourceMention",
      attrs: {
        id: mentionAttrs.id,
        kind: mentionAttrs.kind,
        label: mentionAttrs.label,
        missing: false,
        name: mentionAttrs.name || mentionAttrs.label,
        type: mentionAttrs.type || mentionAttrs.kind,
        url: mentionAttrs.url,
      },
    },
    { type: "text", text: " " },
  ]

  const chain = editor.chain().focus()

  if (range) {
    chain.insertContentAt(range, content).run()
    return
  }

  chain.insertContent(content).run()
}

export const replaceMentionResource = (
  editor: Editor,
  position: number,
  mentionAttrs: MentionNodeAttrs,
): boolean => {
  const node = editor.state.doc.nodeAt(position)

  if (node?.type.name !== "resourceMention") return false

  editor.view.dispatch(
    editor.state.tr.setNodeMarkup(position, undefined, {
      id: mentionAttrs.id,
      kind: mentionAttrs.kind,
      label: mentionAttrs.label,
      missing: false,
      name: mentionAttrs.name || mentionAttrs.label,
      type: mentionAttrs.type || mentionAttrs.kind,
      url: mentionAttrs.url,
    }),
  )

  return true
}

const areMentionAttrsEqual = (
  currentAttrs: MentionNodeAttrs,
  nextAttrs: MentionNodeAttrs,
) => {
  return (
    currentAttrs.id === nextAttrs.id &&
    currentAttrs.kind === nextAttrs.kind &&
    currentAttrs.label === nextAttrs.label &&
    Boolean(currentAttrs.missing) === nextAttrs.missing &&
    currentAttrs.name === nextAttrs.name &&
    currentAttrs.type === nextAttrs.type &&
    currentAttrs.url === nextAttrs.url
  )
}

const MENTION_LABEL_PREFIX: Record<MentionKind, string> = {
  audio: "音频",
  image: "图片",
  video: "视频",
}

export const syncMentionResources = (
  editor: Editor,
  previousResources: UploadedResource[],
  nextResources: UploadedResource[],
  preserveReplacedPositions = true,
): boolean => {
  const previousMentions = getMentionResources(previousResources)
  const nextMentions = getMentionResources(nextResources)
  const nextMentionById = new Map(
    nextMentions.map((resource) => [resource.mentionId, resource]),
  )
  const previousIndexById = new Map(
    previousMentions.map((resource, index) => [resource.mentionId, index]),
  )
  const missingMentionIndexByKind = nextMentions.reduce<
    Record<MentionKind, number>
  >(
    (counts, resource) => {
      counts[resource.kind] += 1
      return counts
    },
    {
      audio: 0,
      image: 0,
      video: 0,
    },
  )
  const missingLabelById = new Map<string, string>()
  const supportsPositionReplacement =
    preserveReplacedPositions && previousMentions.length === nextMentions.length
  let transaction = editor.state.tr

  editor.state.doc.descendants((node, position) => {
    if (node.type.name !== "resourceMention") return true

    const currentAttrs = node.attrs as MentionNodeAttrs
    let nextResource = nextMentionById.get(currentAttrs.id)

    if (!nextResource && supportsPositionReplacement) {
      const previousIndex = previousIndexById.get(currentAttrs.id)
      const positionResource =
        typeof previousIndex === "number"
          ? nextMentions[previousIndex]
          : undefined

      if (positionResource?.kind === currentAttrs.kind) {
        nextResource = positionResource
      }
    }

    let missingLabel = missingLabelById.get(currentAttrs.id)

    if (!nextResource && !missingLabel) {
      missingMentionIndexByKind[currentAttrs.kind] += 1
      missingLabel = `${MENTION_LABEL_PREFIX[currentAttrs.kind]}${missingMentionIndexByKind[currentAttrs.kind]}`
      missingLabelById.set(currentAttrs.id, missingLabel)
    }

    const nextAttrs = nextResource
      ? toMentionAttrs(nextResource)
      : {
          ...currentAttrs,
          label: missingLabel,
          missing: true,
        }

    if (areMentionAttrsEqual(currentAttrs, nextAttrs)) return true

    transaction = transaction.setNodeMarkup(position, undefined, nextAttrs)
    return true
  })

  if (!transaction.docChanged) return false

  if (!preserveReplacedPositions) transaction.setMeta("addToHistory", false)
  editor.view.dispatch(transaction)
  return true
}

export const getEditorPlainText = (editor: Editor) =>
  getPromptNodeText(editor.getJSON())
